import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import WaveSurfer from "wavesurfer.js";

import { animated, useSpring, useTransition } from "@react-spring/web";
import { useDesktop } from "src/components/hooks/useResponsive";
import { RECORD_NEW_RANGE, SYNC_RANGES, UPDATE_RANGE, useAnalytics } from 'src/pages/coaching/components/podcast/components/player/analytics';

import _ from "lodash";
import cx from "classnames";
import styles from "./player.module.scss";
import {
  AudioVisualizerLoader,
  PlayRateButton,
  VolumeButton,
} from "src/pages/coaching/components/podcast/components/player";
import { BannerView } from "src/components/banner";

const CallRecordingView = ({ meeting }) => {
  const {url, duration, initialTimeSpent} = useMemo(() => {
    const audioUrl = _.get(meeting, 'audioUrl') || "";
    const duration = _.get(meeting, 'duration', 0);
    const initialTimeSpent = _.get(meeting, 'timeSpent', 0);

    return {
      url: audioUrl,
      duration: duration,
      initialTimeSpent: initialTimeSpent
    }
  }, [meeting]);
  const segments = 10;

  const containerRef = React.useRef(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);
  const currentRangeRef = React.useRef(null);
  const timerRef = React.useRef<any>(null);

  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor", "#e51058"),
  );

  const desktop = useDesktop();
  const [analytics, dispatch] = useAnalytics(duration, segments);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false,
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const [playerStyles, playerAnimationRef] = useSpring(
    {
      transform: playerState.waveformLoaded
        ? "translateY(0%)"
        : "translateY(100%)",
      opacity: playerState.waveformLoaded ? 1 : 0,
    },
    [playerState.waveformLoaded],
  );

  const title = _.get(meeting, "title", "");

  useEffect(() => {
    if (url && WaveSurfer) {
      loadPlayer();
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error destroying WaveSurfer instance: ", e);
        }

        playerRef.current = null;
      }
    };
  }, [url]);

  const loadPlayer = () => {
    const ctx = document.createElement("canvas").getContext("2d");

    playerRef.current = WaveSurfer.create({
      container: containerRef.current!,
      height: 40,
      waveColor: "#BDBDBD",
      progressColor: primaryColor || "#e51058",
      barWidth: 2,
      barGap: 4,
      barRadius: 2,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });

    playerRef.current.load(url);

    //Event listeners for player
    playerRef.current.on("ready", () => {
      setPlayerState((state) => {
        return {
          ...state,
          duration: Math.round(playerRef.current?.getDuration() || 0),
          waveformLoaded: true,
        };
      });

      playerAnimationRef.start();
      playerRef.current?.setTime(initialTimeSpent || 0);
    });

    playerRef.current.on("play", () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        };
      });
    });

    playerRef.current.on("pause", () => {
      setPlayerState((state) => {
        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
        }
      });
    });

    playerRef.current.on('audioprocess', () => {
      const endRange = playerRef.current?.getCurrentTime() || 0;

      setPlayerState((state) => {
        if ((endRange - state.currentTime) < 0.5) {
          return state;
        }

        if (currentRangeRef.current) {
          currentRangeRef.current = updateRange(playerRef.current?.getCurrentTime()) as any;
        } else {
          currentRangeRef.current = createRange(playerRef.current?.getCurrentTime()) as any;
        }

        return {
          ...state,
          currentTime: Math.round(playerRef.current?.getCurrentTime() || 0)
        }
      });
    });

    playerRef.current.on('seeking', () => {
      setPlayerState((state) => {
        currentRangeRef.current = createRange(playerRef.current?.getCurrentTime()) as any;

        return {
          ...state,
          currentTime: playerRef.current?.getCurrentTime() || 0,
        }
      });
    });

    playerRef.current.on('finish', () => {
      setPlayerState((state) => {
        if (currentRangeRef) {
          const currentTime = playerRef.current?.getCurrentTime() || 0;
          currentRangeRef.current = updateRange(currentTime) as any;
          syncRanges(currentTime);
        }

        return {
          ...state,
          audioPlaying: playerRef.current?.isPlaying() || false,
          currentTime: 0
        }
      });
    });

    playerRef.current.on("error", (e) => {
      setPlayerState((state) => {
        return {
          ...state,
          error: e
        }
      })
    });

    timerRef.current = setInterval(() => {
      const currentTime = playerRef.current?.getCurrentTime() || 0;
      syncRanges(currentTime);
    }, 30000);
  };


  const updateRange = (end) => {
    if (!currentRangeRef.current) {
      return;
    }

    const newRange = {
      ...((currentRangeRef.current ?? {}) as object),
      end: end
    }

    if (!_.isEqual(currentRangeRef.current, newRange)) {
      dispatch({
        type: UPDATE_RANGE,
        range: newRange
      });
    }

    return newRange;
  }

  const syncRanges = (currentTime) => {
    dispatch({
      type: SYNC_RANGES,
      progress: currentTime
    });
  }

  const createRange = (start) => {
    const newRange = {
      id: new Date().getTime(),
      external_id: null,
      start: start,
      end: start,
      segmentLength: segments,
      synced: false,
      syncing: false,
      updatedDate: new Date().getTime(),
      syncedDate: null
    };

    dispatch({
      type: RECORD_NEW_RANGE,
      progress: start,
      range: newRange
    });

    return newRange;
  }

  const onPlayPause = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.playPause();
    }
  }, [playerRef.current]);

  const onReplay = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }
  }, [playerRef.current]);

  const handleSkipBackward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() - 15; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);

  const handleSkipForward = useCallback(() => {
    if (playerRef.current) {
      const newTime = playerRef.current.getCurrentTime() + 30; // Adjust this value as needed
      playerRef.current.seekTo(newTime / playerRef.current.getDuration());
    }
  }, [playerRef.current]);

  const handlePlaybackRateChange = useCallback(
    (rate) => {
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(rate);
        setPlayerState((state) => {
          return {
            ...state,
            playbackRate: rate,
          };
        });
      }
    },
    [playerRef.current],
  );

  const handleVolumeChange = useCallback(
    (volume) => {
      if (playerRef.current) {
        playerRef.current.setVolume(volume / 100);
        setPlayerState((state) => {
          return {
            ...state,
            volume: volume,
          };
        });
      }
    },
    [playerRef.current],
  );

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = String(mins).padStart(2, "0");
    const paddedSecs = String(secs).padStart(2, "0");

    if (hrs === 0) {
      return `${paddedMins}:${paddedSecs}`;
    } else {
      const paddedHrs = String(hrs);
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.detailsCard}>
        <div className={styles.summaryRow}>
          <div className={cx(styles.field, styles.thumbnail)}>
            <div className={styles.inner}>
              <BannerView
                banner={_.get(meeting, "thumbnailUrl")}
                defaultBanner="/images/coaching/recording.png"
                overlay={false}
              />
            </div>
          </div>
          {url && (<div className={cx(styles.field, styles.name)}>
            <span>{title}</span>
          </div>)}
          {!url && (
            <div className={styles.noAudio}>
              No audio availbale!
            </div>
          )}
        </div>
      </div>

      {url && 
      (<div className={cx(styles.podcastPlayerContainer)}>
        {loadingTransition(
          (style, item) =>
            item && (
              <animated.div className={styles.loading} style={style}>
                <AudioVisualizerLoader />
              </animated.div>
            ),
        )}

        <animated.div
          className={cx(styles.podcastPlayer, {
            [styles.waveformLoaded]: playerState.waveformLoaded,
          })}
          style={{
            transform: playerState.waveformLoaded
              ? "translateY(0%)"
              : "translateY(100%)",
            opacity: playerState.waveformLoaded ? 1 : 0,
          }}
        >
          {!desktop && (
            <div className={cx(styles.row, styles.mobileTrack)}>
              <div className={styles.inner}>
                <div ref={containerRef} className={styles.waveform} />
              </div>
              <div className={styles.time}>
                <div className={styles.timeElapsed}>
                  {formatTime(playerState.currentTime || 0)}
                </div>
                <div className={styles.totalTime}>
                  {formatTime(playerState.duration || 0)}
                </div>
              </div>
            </div>
          )}
          <div className={cx(styles.row, styles.player)}>
            <div className={cx(styles.actions, styles.left)}></div>
            <div className={cx(styles.actions, styles.main)}>
              <button
                onClick={handleSkipBackward}
                className={cx(styles.action, styles.rewind)}
              >
                <i className="paperflite-reload"></i>
                <span>15</span>
              </button>
              <button
                onClick={onPlayPause}
                className={cx(styles.action, styles.play)}
              >
                <i
                  className={
                    playerState.audioPlaying
                      ? "paperflite-pause"
                      : "paperflite-play"
                  }
                ></i>
              </button>
              <button
                onClick={handleSkipForward}
                className={cx(styles.action, styles.forward)}
              >
                <i className="paperflite-reload"></i>
                <span>30</span>
              </button>
            </div>
            {desktop && (
              <div className={styles.track}>
                <div className={styles.inner}>
                  <div className={styles.timeElapsed}>
                    {formatTime(playerState.currentTime || 0)}
                  </div>
                  <div ref={containerRef} className={styles.waveform} />
                  <div className={styles.totalTime}>
                    {formatTime(playerState.duration || 0)}
                  </div>
                </div>
              </div>
            )}
            <div className={cx(styles.actions, styles.right)}>
              <VolumeButton
                volume={playerState.volume}
                onChange={(value) => {
                  handleVolumeChange(value);
                }}
              />
              <PlayRateButton
                playbackRate={playerState.playbackRate}
                onChange={handlePlaybackRateChange}
              />
            </div>
          </div>
        </animated.div>
      </div>)}
    </section>
  );
};

export default CallRecordingView;
